import '../App.css';
import {
    Modal,
    Button,
    ListGroup,
    Accordion,
    AccordionHeader,
    AccordionBody,
    Stack,
    Card,
    CardFooter,
} from 'react-bootstrap';
import CustomTooltip from './CustomTooltip';
import Team from '../schemas/Team';
import Manager from '../schemas/Manager';
import Sleeper from '../schemas/Sleeper';
import { DraftPick } from '../schemas/DraftRound';
import { VeteranPick } from '../schemas/VeteranDraft';
import { SeasonTrades } from '../schemas/Trade';
import { managers } from '../data/managers';
import React, {
    Dispatch,
    SetStateAction,
    useState,
    useEffect,
    useCallback
} from 'react';
import {
    numsArrAvgCalc,
    mapManagerToTeam,
} from '../utils';

interface ModalProps {
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
    fullscreen: boolean;
    team: Team;
}

function TeamModal({ team, show, setShow }: ModalProps) {
    const [currentName, setCurrentName] = useState("");
    const [prevTeamNames, setPrevTeamNames] = useState("");
    const [noPrevNames, setNoPrevNames] = useState(false);
    const [currentManager, setCurrentManager] = useState("");
    const [prevManagers, setPrevManagers] = useState([""]);
    const [noPrevManagers, setNoPrevManagers] = useState(false);
    const [allTeamManagers, setAllTeamManagers] = useState<Manager[]>([]);
    const [managerToView, setManagerToView] = useState<Manager | undefined>(undefined);
    const [avgRank, setAvgRank] = useState(0);
    const [avgDraftPick, setAvgDraftPick] = useState(0);
    const [maxSnakePick, setMaxSnakePick] = useState(0);
    const [topSnakePicksCount, setTopSnakePicksCount] = useState(0);
    const [bestSnakePicks, setBestSnakePicks] = useState<DraftPick[] | undefined>([]);
    const [bestSnakePicksCount, setBestSnakePicksCount] = useState(0);
    const [worstSnakePicks, setWorstSnakePicks] = useState<DraftPick[] | undefined>([]);
    const [worstSnakePicksCount, setWorstSnakePicksCount] = useState(0);
    const [avgVetDraft, setAvgVetDraft] = useState("");
    const [maxVetPick, setMaxVetPick] = useState(0);
    const [topVetPickCount, setTopVetPickCount] = useState(0);
    const [bestVetPicks, setBestVetPicks] = useState<VeteranPick[] | undefined>([]);
    const [bestVetPicksCount, setBestVetPicksCount] = useState(0);
    const [worstVetPicks, setWorstVetPicks] = useState<VeteranPick[] | undefined>([]);
    const [worstVetPicksCount, setWorstVetPicksCount] = useState(0);
    const [goldMedals, setGoldMedals] = useState([""]);
    const [silverMedals, setSilverMedals] = useState([""]);
    const [bronzeMedals, setBronzeMedals] = useState([""]);
    const [totalTrades, setTotalTrades] = useState(0);
    const [bestTrades, setBestTrades] = useState<SeasonTrades[]>([]);
    const [bestTradesCount, setBestTradesCount] = useState(0);
    const [worstTrades, setWorstTrades] = useState<SeasonTrades[]>([]);
    const [worstTradesCount, setWorstTradesCount] = useState(0);
    const [bestSleepers, setBestSleepers] = useState<Sleeper[]>([]);
    const [bestSleepersCount, setBestSleepersCount] = useState(0);
    const [worstSleepers, setWorstSleepers] = useState<Sleeper[]>([]);
    const [worstSleepersCount, setWorstSleepersCount] = useState(0);

    useEffect(() => {
        const filteredManagers = [...managers].filter((manager) => {
            return team.manager.includes(manager.name);
        });
        setAllTeamManagers(filteredManagers);

        setCurrentName(team.teamName[(team.teamName).length - 1]);
        setCurrentManager(team.manager[(team.manager).length - 1]);
        if ((team.teamName).length > 1) {
            const teamNamesCopy = [...team.teamName];
            teamNamesCopy.pop();
            if (teamNamesCopy) {
                const prevNames = teamNamesCopy.join(", ");
                setPrevTeamNames(prevNames);
            }
        } else {
            setNoPrevNames(true);
        };

        if ((team.manager).length > 1) {
            const teamManagersCopy = [...team.manager];
            teamManagersCopy.pop();
            if (teamManagersCopy) {
                teamManagersCopy.reverse();
                setPrevManagers(teamManagersCopy);
            }
        } else {
            setNoPrevManagers(true);
        };

        setAvgRank(numsArrAvgCalc(team.rankings));
        setAvgDraftPick(numsArrAvgCalc(team.draftPositions));
        setMaxSnakePick(Math.min(...team.draftPositions));
        if (maxSnakePick < 4) {
            const amount = [...team.draftPositions].filter(pos => pos === maxSnakePick).length;
            setTopSnakePicksCount(amount);
        }
        if (team.vetDraftPos) {
            const sumVetPicks = [...team.vetDraftPos].reduce((a, b) => a + b, 0);
            setAvgVetDraft((sumVetPicks / (team.vetDraftPos).length).toFixed(2));
            setMaxVetPick(Math.min(...team.vetDraftPos));
            if (maxVetPick < 4) {
                const amount = [...team.vetDraftPos].filter(pos => pos === maxVetPick).length;
                setTopVetPickCount(amount)

            }
        };

        podiumCounter(team.rankings);

    }, [team, managers]);

    const podiumCounter = (ranks: number[]) => {
        let goldMedalsArr: string[] = [];
        let silverMedalsArr: string[] = [];
        let bronzeMedalsArr: string[] = [];

        for (let i = 0; i < ranks.length; i++) {
            switch (ranks[i]) {
                case 1:
                    goldMedalsArr.push('../1st-place.png');
                    break;
                case 2:
                    silverMedalsArr.push('../2nd-place.png');
                    break;
                case 3:
                    bronzeMedalsArr.push('../3rd-place.png');
                    break;
            }
        };
        setGoldMedals(goldMedalsArr);
        setSilverMedals(silverMedalsArr);
        setBronzeMedals(bronzeMedalsArr);
    }

    const handleManagerClick = useCallback(
        (e: React.MouseEvent) => {
            const clickedElement = e.target as HTMLElement;
            const clickedManagerName = clickedElement.textContent?.trim() || "";
            const clickedManagerData = allTeamManagers.find((manager) =>
                manager.name === clickedManagerName
            );
            if (clickedManagerData) {
                podiumCounter(clickedManagerData.rankings);
                setManagerToView(clickedManagerData);
                if (clickedManagerData.trades) {
                    const allTrades = [...clickedManagerData.trades].reduce((sum, seasonData) => {
                        return sum + seasonData.allTrades.length;
                    }, 0);
                    setTotalTrades(allTrades);

                    const bestTrades: SeasonTrades[] = [...clickedManagerData.trades]
                        .filter((season) => {
                            return season.allTrades.some((trade) =>
                                trade.amongBestTrades
                            );
                        })
                        .map((season) => {
                            const bestTrades = season.allTrades.filter((trade) =>
                                trade.amongBestTrades
                            );

                            return {
                                season: season.season,
                                allTrades: bestTrades
                            };
                        });
                    setBestTrades(bestTrades);
                    setBestTradesCount(bestTrades.length)
                    const worstTrades: SeasonTrades[] = [...clickedManagerData.trades]
                        .filter((season) => {
                            return season.allTrades.some((trade) =>
                                trade.amongWorstTrades
                            );
                        })
                        .map((season) => {
                            const worstTrades = season.allTrades.filter((trade) =>
                                trade.amongWorstTrades
                            );

                            return {
                                season: season.season,
                                allTrades: worstTrades
                            };
                        });
                    setWorstTrades(worstTrades);
                    setWorstTradesCount(worstTrades.length)
                };

                if (clickedManagerData.bestSnakePicks) {
                    setBestSnakePicks(clickedManagerData.bestSnakePicks);
                    setBestSnakePicksCount(clickedManagerData.bestSnakePicks.length);
                } else {
                    setBestSnakePicks([]);
                    setBestSnakePicksCount(0);
                };
                if (clickedManagerData.worstSnakePicks) {
                    setWorstSnakePicks(clickedManagerData.worstSnakePicks);
                    setWorstSnakePicksCount(clickedManagerData.worstSnakePicks.length);
                } else {
                    setWorstSnakePicks([]);
                    setWorstSnakePicksCount(0);
                }
                if (clickedManagerData.bestVetPicks) {
                    setBestVetPicks(clickedManagerData.bestVetPicks);
                    setBestVetPicksCount(clickedManagerData.bestVetPicks.length);
                } else {
                    setBestVetPicks([]);
                    setBestVetPicksCount(0);
                };
                if (clickedManagerData.worstVetPicks) {
                    setWorstVetPicks(clickedManagerData.worstVetPicks);
                    setWorstVetPicksCount(clickedManagerData.worstVetPicks.length);
                } else {
                    setWorstVetPicks([]);
                    setWorstVetPicksCount(0);
                }

                if (clickedManagerData.notableSleepers) {
                    const bestSleepers: Sleeper[] = [...clickedManagerData.notableSleepers]
                        .filter((sleeper) =>
                            sleeper.amongBestSleepers
                        );
                    const sortedBestSleepers = [...bestSleepers].sort((a, b) => {
                        const yearA = parseInt(a.seasonPicked.split('/')[0], 10);
                        const yearB = parseInt(b.seasonPicked.split('/')[0], 10);

                        return yearA - yearB;
                    });

                    setBestSleepers(sortedBestSleepers);
                    setBestSleepersCount(bestSleepers.length);

                    const worstSleepers: Sleeper[] = [...clickedManagerData.notableSleepers]
                        .filter((sleeper) =>
                            sleeper.amongWorstSleepers
                        );
                    setWorstSleepers(worstSleepers);
                    setWorstSleepersCount(worstSleepers.length);
                }
            }
        },
        [allTeamManagers, setManagerToView, setTotalTrades, setBestTrades, setBestTradesCount, setWorstTrades, setWorstTradesCount, setBestSnakePicks, setBestSnakePicksCount, setWorstSnakePicks, setWorstSnakePicksCount, setBestVetPicks, setBestVetPicksCount, setWorstVetPicks, setWorstVetPicksCount,
            setBestSleepers, setBestSleepersCount, setWorstSleepers, setWorstSleepersCount]
    );

    return (
        <Modal show={show} fullscreen>
            <div className='d-flex justify-content-between' style={team.headerColors}>
                <Modal.Title
                    dir='ltr'
                    className='mt-2 ms-3 fs-3 fw-bold'

                >
                    {currentName}
                </Modal.Title>
                <Button
                    variant="light"
                    aria-label="Close"
                    onClick={() => setShow(false)}
                    className='mt-3 me-5'
                >
                    סגירה
                </Button>
            </div>

            <Modal.Header
                className='d-flex justify-content-between'
                dir='rtl'
                style={team.headerColors}
            >



                {team.currentKeepers !== undefined &&
                    team.currentKeepers.map((keeper, idx) => (
                        <Stack
                            direction="horizontal"
                            gap={1}
                            key={idx}
                            className='me-2'
                        >
                            <CustomTooltip  title={keeper.onHover}>
                            <Card>
                                <Card.Img variant='top'
                                    src={keeper.picUrl}
                                />
                                <CardFooter className='text-center fw-bold fst-italic'>
                                    {keeper.name}
                                </CardFooter>
                            </Card>
                            </CustomTooltip>
                        </Stack>

                    ))}

                <div className='d-flex justify-content-between mt-3'>

                    {
                        allTeamManagers.length > 1 &&
                        managerToView &&
                        <h4 className='me-3 mt-2'>
                            <span>
                                נתוני מנג'ר:&nbsp;
                            </span>
                            {managerToView.name}
                        </h4>
                    }


                </div>



            </Modal.Header>
            <div className='d-flex justify-content-evenly mt-3'>
                {managerToView && (
                    <div className='w-50' dir='rtl'>
                        <ListGroup variant="flush">
                            <ListGroup.Item>
                                <span className='fw-semibold'>
                                    הצטרף בעונת:&nbsp;
                                </span>
                                <span>
                                    {managerToView.seasonJoined}
                                </span>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <span className='fw-semibold'>
                                    ותק:&nbsp;
                                </span>
                                <span>
                                    {managerToView.totalSeasons} עונות
                                </span>
                            </ListGroup.Item>
                            {
                                allTeamManagers.length > 1 &&
                                <>
                                    <ListGroup.Item>
                                        <span className='fw-semibold'>
                                            דירוג ממוצע:&nbsp;
                                        </span>
                                        <span>
                                            {numsArrAvgCalc(managerToView.rankings)}
                                        </span>
                                    </ListGroup.Item>

                                    <ListGroup.Item>
                                        <span className='fw-semibold'>
                                            מד פודיום:&nbsp;</span>{
                                            goldMedals.length > 0 && (
                                                <>
                                                    {goldMedals.map((el, idx) => (
                                                        <img
                                                            src={el}
                                                            key={idx}
                                                            style={{
                                                                height: "35px",
                                                                width: "35px",
                                                                marginLeft: "5px"
                                                            }}
                                                            alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                                        />
                                                    ))}
                                                </>
                                            )
                                        }
                                        {silverMedals.length > 0 && (
                                            <>
                                                {silverMedals.map((el, idx) => (
                                                    <img
                                                        src={el}
                                                        key={idx}
                                                        style={{
                                                            height: "35px",
                                                            width: "35px",
                                                            marginLeft: "5px"
                                                        }}
                                                        alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                                    />
                                                ))}
                                            </>
                                        )
                                        }
                                        {bronzeMedals.length > 0 && (
                                            <>
                                                {bronzeMedals.map((el, idx) => (
                                                    <img
                                                        src={el}
                                                        key={idx}
                                                        style={{
                                                            height: "35px",
                                                            width: "35px",
                                                            marginLeft: "5px"
                                                        }}
                                                        alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                                    />
                                                ))}
                                            </>
                                        )
                                        }
                                        {
                                            goldMedals.length === 0 &&
                                            silverMedals.length === 0 &&
                                            bronzeMedals.length === 0 &&
                                            (
                                                <span>
                                                    לא זכה באף מדליה
                                                </span>
                                            )
                                        }
                                    </ListGroup.Item>
                                    <ListGroup.Item>
                                        <span className='fw-semibold'>
                                            מיקום ממוצע בסנייק:&nbsp;
                                        </span>
                                        <span>
                                            {numsArrAvgCalc(managerToView.draftPositions)}
                                        </span>
                                    </ListGroup.Item>
                                    {
                                        managerToView.vetDraftPos !== undefined && (
                                            <ListGroup.Item>
                                                <span className='fw-semibold'>
                                                    מיקום ממוצע בדראפט וטרנים:&nbsp;
                                                </span>
                                                <span >
                                                    {
                                                        managerToView.vetDraftPos !== undefined &&
                                                        numsArrAvgCalc(managerToView.vetDraftPos)
                                                    }
                                                </span>
                                            </ListGroup.Item>
                                        )}
                                </>
                            }

                            <ListGroup.Item>
                                <span className='fw-semibold'>
                                    ממוצע מהלכים בעונה:&nbsp;
                                </span>
                                <span>
                                    {numsArrAvgCalc(managerToView.movesPerSeason)}
                                </span>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <span className='fw-semibold'>
                                    ממוצע טריידים בעונה:&nbsp;
                                </span>
                                <span>
                                    {parseFloat((totalTrades / managerToView.totalSeasons).toFixed(2))}
                                </span>

                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Accordion>
                                    <Accordion.Item eventKey='0'>
                                        <Accordion.Header className='fw-semibold mb-2'>
                                            <span className='fw-semibold'>
                                                טריידים בולטים לטובה
                                                {bestTradesCount > 0 && (
                                                    <span>&nbsp;({bestTradesCount})</span>
                                                )}
                                            </span>
                                            <span className='fw-normal'>
                                                {bestTrades.length === 0 && ": לא נמצאו"}
                                            </span>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Accordion>
                                                {bestTrades !== undefined &&
                                                    bestTrades.map((el, idx) => (
                                                        <Accordion.Item eventKey={idx.toString()} key={idx}>
                                                            <Accordion.Header>
                                                                עונת {el.season}&nbsp;
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                {el.allTrades &&
                                                                    el.allTrades.map((trade, tradeIdx) => (
                                                                        <div key={tradeIdx} className='mb-3'>
                                                                            <div>
                                                                                מול: {trade.otherTeam}&nbsp;
                                                                                ({mapManagerToTeam(trade.otherTeam, el.season)})
                                                                            </div>
                                                                            <div>
                                                                                נתן: {trade.sentAway}
                                                                            </div>
                                                                            <div>
                                                                                קיבל: {trade.gotInReturn}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    ))}
                                            </Accordion>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                                <Accordion>
                                    <Accordion.Item eventKey='0'>
                                        <Accordion.Header className=' mb-2'>
                                            <span className='fw-semibold'>
                                                טריידים בולטים לרעה
                                            </span>
                                            {worstTradesCount > 0 && <span className='fw-semibold'
                                            >
                                                &nbsp;({worstTradesCount})</span>}
                                            <span className='fw-normal'>
                                                {worstTrades.length === 0 && ": לא נמצאו"}
                                            </span>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Accordion>
                                                {worstTrades !== undefined &&
                                                    worstTrades.map((el, idx) => (
                                                        <Accordion.Item eventKey={idx.toString()} key={idx}>
                                                            <Accordion.Header>
                                                                עונת {el.season}&nbsp;
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                {el.allTrades &&
                                                                    el.allTrades.map((trade, tradeIdx) => (
                                                                        <div key={tradeIdx} className='mb-3'>
                                                                            <div>
                                                                                מול: {trade.otherTeam}&nbsp;
                                                                                ({mapManagerToTeam(trade.otherTeam, el.season)})
                                                                            </div>
                                                                            <div>
                                                                                נתן: {trade.sentAway}
                                                                            </div>
                                                                            <div>
                                                                                קיבל: {trade.gotInReturn}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    ))}
                                            </Accordion>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </ListGroup.Item>

                            <ListGroup.Item>
                                <Accordion>
                                    <AccordionHeader>
                                        <span className='fw-semibold'>
                                            בחירות בולטות לטובה מסיבובים 1-2
                                        </span>
                                        {bestSnakePicksCount > 0 && <span
                                            className='fw-semibold'
                                        >
                                            &nbsp;({bestSnakePicksCount})
                                        </span>}&nbsp;
                                        <span className='fw-normal'>
                                            {bestSnakePicksCount === 0 && ": לא נמצאו"}
                                        </span>
                                    </AccordionHeader>
                                    {
                                        bestSnakePicks !== undefined &&
                                        bestSnakePicks?.map((pick, idx) => (
                                            <AccordionBody key={idx}>
                                                {pick.draftedPlayer}&nbsp;
                                                (בחירה מס' {pick.pickNumber} בעונת {pick.season})&nbsp;&nbsp;
                                                {
                                                    pick.becameKeeper ? (
                                                        <span
                                                            style={
                                                                {
                                                                    fontSize: "10px",
                                                                    fontWeight: "bolder",
                                                                    border: "1px solid black",
                                                                    padding: "2px",
                                                                    marginRight: "7px"
                                                                }
                                                            }
                                                        >
                                                            K
                                                        </span>

                                                    ) : ''
                                                }
                                                {
                                                    pick.wasSleeper ? (
                                                        <span
                                                            className='fst-italic border border-danger p-2 bg-danger text-white rounded-pill'>
                                                            Sleeper
                                                        </span>

                                                    ) : ''
                                                }
                                            </AccordionBody>
                                        ))
                                    }
                                </Accordion>
                            </ListGroup.Item>
                            <ListGroup.Item>
                                <Accordion>
                                    <AccordionHeader>
                                        <span className='fw-semibold'>
                                            בחירות בולטות לרעה מסיבובים 1-2
                                        </span>
                                        {worstSnakePicksCount > 0 && <span
                                            className='fw-semibold'
                                        >
                                            &nbsp;({worstSnakePicksCount})
                                        </span>}&nbsp;
                                        <span className='fw-normal'>
                                            {worstSnakePicksCount === 0 && ": לא נמצאו"}
                                        </span>
                                    </AccordionHeader>
                                    {
                                        worstSnakePicks !== undefined &&
                                        worstSnakePicks?.map((pick, idx) => (
                                            <AccordionBody key={idx}>
                                                {pick.draftedPlayer}&nbsp;
                                                (בחירה מס' {pick.pickNumber} בעונת {pick.season})&nbsp;&nbsp;
                                                {
                                                    pick.wasSleeper ? (
                                                        <span
                                                            className='fst-italic border border-danger p-2 bg-danger text-white rounded-pill'>
                                                            Sleeper
                                                        </span>

                                                    ) : ''
                                                }

                                            </AccordionBody>
                                        ))
                                    }
                                </Accordion>
                            </ListGroup.Item>

                            {
                                managerToView.bestVetPicks !== undefined && (
                                    <ListGroup.Item>
                                        <Accordion>
                                            <AccordionHeader>
                                                <span className='fw-semibold'>
                                                    בחירות וטרנים בולטות לטובה
                                                </span>
                                                {bestSnakePicksCount > 0 && <span
                                                    className='fw-semibold'
                                                >
                                                    &nbsp;({bestVetPicksCount})
                                                </span>}&nbsp;
                                                <span className='fw-normal'>
                                                    {bestVetPicksCount === 0 && ": לא נמצאו"}
                                                </span>
                                            </AccordionHeader>
                                            {
                                                bestVetPicks !== undefined &&
                                                bestVetPicks?.map((pick, idx) => (
                                                    <AccordionBody key={idx}>
                                                        {pick.player}&nbsp;
                                                        (בחירה מס' {pick.position} בעונת {pick.season})

                                                    </AccordionBody>
                                                ))
                                            }
                                        </Accordion>
                                    </ListGroup.Item>
                                )
                            }
                            {
                                managerToView.worstVetPicks !== undefined && (
                                    <ListGroup.Item>
                                        <Accordion>
                                            <AccordionHeader>
                                                <span className='fw-semibold'>
                                                    בחירות וטרנים בולטות לרעה
                                                </span>
                                                {worstVetPicksCount > 0 && <span
                                                    className='fw-semibold'
                                                >
                                                    &nbsp;({worstVetPicksCount})
                                                </span>}&nbsp;
                                                <span className='fw-normal'>
                                                    {worstVetPicksCount === 0 && ": לא נמצאו"}
                                                </span>
                                            </AccordionHeader>
                                            {
                                                worstVetPicks !== undefined &&
                                                worstVetPicks?.map((pick, idx) => (
                                                    <AccordionBody key={idx}>
                                                        {pick.player}&nbsp;
                                                        (בחירה מס' {pick.position} בעונת {pick.season})

                                                    </AccordionBody>
                                                ))
                                            }
                                        </Accordion>
                                    </ListGroup.Item>
                                )}
                            {
                                bestSleepers !== undefined && (
                                    <ListGroup.Item>
                                        <Accordion>
                                            <AccordionHeader>
                                                <span className='fw-semibold'>
                                                    סליפרים בולטים לטובה
                                                </span>
                                                {bestSleepersCount > 0 && <span
                                                    className='fw-semibold'
                                                >
                                                    &nbsp;({bestSleepersCount})
                                                </span>}&nbsp;
                                                <span className='fw-normal'>
                                                    {bestSleepersCount === 0 && ": לא נמצאו"}
                                                </span>
                                            </AccordionHeader>
                                            <AccordionBody>
                                                {
                                                    bestSleepers.map((sleeper) =>
                                                        <Accordion>
                                                            <AccordionHeader>
                                                                {sleeper.name}&nbsp;
                                                                ({sleeper.seasonPicked})
                                                                &nbsp;
                                                            </AccordionHeader>
                                                            <AccordionBody>
                                                                <div
                                                                    className='d-flex flex-column'
                                                                >
                                                                    <div>
                                                                        &ndash; {sleeper.pickedUpVia}
                                                                    </div>

                                                                    {
                                                                        sleeper.madeFourthYear && (
                                                                            <div>
                                                                                &ndash; הושבח לעונה רביעית
                                                                            </div>
                                                                        )
                                                                    }
                                                                    {
                                                                        sleeper.becameFreeKeeper && (
                                                                            <div>
                                                                                &ndash; קודם למשבצת קיפר חופשי
                                                                            </div>
                                                                        )
                                                                    }
                                                                    {
                                                                        sleeper.keptUntil && (
                                                                            <div>
                                                                                &ndash; הושאר עד: {sleeper.keptUntil}
                                                                            </div>
                                                                        )
                                                                    }

                                                                    {
                                                                        sleeper.valueYielded && (
                                                                            <div>
                                                                                &ndash; {sleeper.valueYielded}
                                                                            </div>
                                                                        )
                                                                    }

                                                                </div>

                                                            </AccordionBody>
                                                        </Accordion>
                                                    )
                                                }                                                                                </AccordionBody>
                                        </Accordion>
                                    </ListGroup.Item>
                                )
                            }

                            {
                                worstSleepers !== undefined && (
                                    <ListGroup.Item>
                                        <Accordion>
                                            <AccordionHeader>
                                                <span className='fw-semibold'>
                                                    סליפרים בולטים לרעה
                                                </span>
                                                {worstSleepersCount > 0 && <span
                                                    className='fw-semibold'
                                                >
                                                    &nbsp;({worstSleepersCount})
                                                </span>}&nbsp;
                                                <span className='fw-normal'>
                                                    {worstSleepersCount === 0 && ": לא נמצאו"}
                                                </span>
                                            </AccordionHeader>
                                            <AccordionBody>
                                                {
                                                    worstSleepers.map((sleeper) =>
                                                        <Accordion>
                                                            <AccordionHeader>
                                                                {sleeper.name}&nbsp;
                                                                ({sleeper.seasonPicked})
                                                                &nbsp;
                                                            </AccordionHeader>
                                                            <AccordionBody>
                                                                <div
                                                                    className='d-flex flex-column'
                                                                >
                                                                    <div>
                                                                        &ndash; {sleeper.pickedUpVia}
                                                                    </div>

                                                                    {
                                                                        sleeper.madeFourthYear && (
                                                                            <div>
                                                                                &ndash; הושבח לעונה רביעית
                                                                            </div>
                                                                        )
                                                                    }
                                                                    {
                                                                        sleeper.keptUntil && (
                                                                            <div>
                                                                                &ndash; הושאר עד: {sleeper.keptUntil}
                                                                            </div>
                                                                        )
                                                                    }


                                                                </div>

                                                            </AccordionBody>
                                                        </Accordion>
                                                    )
                                                }                                                                                </AccordionBody>
                                        </Accordion>
                                    </ListGroup.Item>
                                )
                            }
                        </ListGroup>
                    </div>
                )}

                <Modal.Body dir='rtl' className='me-3'>
                    <ListGroup variant="flush">
                        <ListGroup.Item
                            className={noPrevNames ? 'd-none' : 'd-block'}
                        >
                            <span className='fw-semibold'>שמות קודמים:&nbsp;</span>
                            <span>
                                {prevTeamNames}
                            </span>
                        </ListGroup.Item>
                        <ListGroup.Item><span className='fw-semibold'>מנג'ר:&nbsp;</span>
                            <span
                                className='manager-span border border-2 border-primary-subtle rounded'

                                onClick={(e) => handleManagerClick(e)}
                            >
                                &nbsp;{currentManager}&nbsp;
                            </span>
                            <span
                                className='tiny-font'
                            >
                                לחץ על שם מנג'ר לצפייה בנתונים הספציפיים שלו
                            </span>

                        </ListGroup.Item>

                        <ListGroup.Item
                            className={noPrevManagers ? 'd-none' : 'd-block'}
                        >
                            <span
                                className='fw-semibold'

                            >מנג'רים קודמים:&nbsp;
                            </span>
                            {
                                prevManagers.length &&
                                prevManagers.map((manager, idx) => (
                                    <>
                                        <span
                                            key={idx}
                                            className='manager-span border border-2 border-primary-subtle rounded ms-2'
                                            onClick={(e) => handleManagerClick(e)}
                                        >
                                            &nbsp;{manager}&nbsp;
                                        </span>
                                    </>
                                ))
                            }
                        </ListGroup.Item>
                        <ListGroup.Item><span className='fw-semibold'>דירוג ממוצע:&nbsp;</span>
                            {avgRank}</ListGroup.Item>
                        <ListGroup.Item>
                            <span className='fw-semibold'>
                                מד פודיום:&nbsp;</span>{
                                goldMedals.length > 0 && (
                                    <>
                                        {goldMedals.map((el, idx) => (
                                            <img
                                                src={el}
                                                key={idx}
                                                style={{
                                                    height: "35px",
                                                    width: "35px",
                                                    marginLeft: "5px"
                                                }}
                                                alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                            />
                                        ))}
                                    </>
                                )
                            }
                            {silverMedals.length > 0 && (
                                <>
                                    {silverMedals.map((el, idx) => (
                                        <img
                                            src={el}
                                            key={idx}
                                            style={{
                                                height: "35px",
                                                width: "35px",
                                                marginLeft: "5px"
                                            }}
                                            alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                        />
                                    ))}
                                </>
                            )
                            }
                            {bronzeMedals.length > 0 && (
                                <>
                                    {bronzeMedals.map((el, idx) => (
                                        <img
                                            src={el}
                                            key={idx}
                                            style={{
                                                height: "35px",
                                                width: "35px",
                                                marginLeft: "5px"
                                            }}
                                            alt='https://www.freepik.com/icons/gold-trophy" Icon by Md Tanvirul Haque'
                                        />
                                    ))}
                                </>
                            )
                            }

                        </ListGroup.Item>
                        <ListGroup.Item>
                            <span className='fw-semibold'>מיקום ממוצע בסנייק:&nbsp;</span>
                            {avgDraftPick}</ListGroup.Item>

                        <ListGroup.Item>
                            <span className='fw-semibold'>בחירת סנייק הגבוהה ביותר:&nbsp;</span>
                            <span>
                                {maxSnakePick},&nbsp;
                            </span>
                            {
                                topSnakePicksCount > 1 ?
                                    `${topSnakePicksCount} פעמים` : 'פעם אחת'

                            }
                        </ListGroup.Item>
                        <ListGroup.Item><span className='fw-semibold'>מיקום ממוצע בדראפט וטרנים (החל מעונת 2016/17 והלאה):&nbsp;</span>
                            {parseFloat(avgVetDraft)}</ListGroup.Item>
                        <ListGroup.Item><span className='fw-semibold'>בחירת דראפט וטרנים הגבוהה ביותר:&nbsp;</span>
                            <span>
                                {maxVetPick},&nbsp;
                            </span>
                            {
                                topVetPickCount > 1 ?
                                    `${topVetPickCount} פעמים` : 'פעם אחת'
                            }
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <span className='fw-semibold'>שישיית קיפרים בעונת הג'נסיס:&nbsp;</span>
                            <br />
                            <span>
                                {team.initialFranchisePlayers[0]}
                            </span>
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <span className='fw-semibold'>רביעיית שחקני פרנצ'ייז בעונת 2010/11:&nbsp;</span>
                            <br />
                            <span
                                style={{ textWrap: 'nowrap' }}
                            >
                                {team.initialFranchisePlayers[1]}
                            </span>
                        </ListGroup.Item>
                        <ListGroup.Item>
                            <span className='fw-semibold'>שחקני פרנצ'ייז החל מעונת 2011/12 והלאה:&nbsp;</span>
                            <Accordion>
                                {
                                    team.singleFranchisePlayer && (
                                        team.singleFranchisePlayer.map((player, idx) => (
                                            <Accordion.Item eventKey={idx.toString()} key={player.id}>
                                                <Accordion.Header>{player.name}&nbsp; </Accordion.Header>
                                                <Accordion.Body>
                                                    <span className='fw-semibold'>צורף באמצעות:&nbsp;</span>
                                                    {player.aquiredVia}
                                                    <br />
                                                    <span className='fw-semibold'>מספר עונות בתפקיד:&nbsp;</span>
                                                    {player.tenure}
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))
                                    )}
                            </Accordion>
                        </ListGroup.Item>
                    </ListGroup>
                </Modal.Body>
            </div>
        </Modal >
    )
}

export default TeamModal;